import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid, Input, Modal } from "antd";
import { toast } from "react-toastify";
import { AiOutlineSearch } from "react-icons/ai";
import { Helmet } from "react-helmet-async";
import StudentLayout from "../../layout/StudentLayout";
import Layout from "../../layout/Layout";
import CenterLoading from "../../components/loader/CenterLoader";
import Pagination from "../../components/pagination/Pagination";
import CreateOfferCard from "../../components/createOfferCard/CreateOfferCard";
import FiverrCard from "./FiverrCard";
import FilterSidebar from "./FilterSidebar";
import TeacherDetailPopup from "./../../components/teacher-detail-popup/index";
import { fetchCoursesDataOnSearch2 } from "../../services/course";
import { fetchCityArea } from "../../services/areas";
import { fetchLimitedCities } from "../../services/cities";
import { fetchCourseLevels } from "../../services/course-levels";
import { fetchQualificationLevels } from "../../services/qualification";
import { fetchEducationBoards } from "../../services/board";
import {
  getTeacherReviews,
  fetchTeacherEducations,
  fetchTeacherAcademicExperiance,
  fetchTeacherCorporateExperiance,
  fetchTeacherTeachingBoards,
  fetchTeacherDocuments,
} from "../../services/teacher";
import { addDocToFirestore, filterProfilePicture } from "../../helper";
import {
  DEMO_TYPE,
  OFFER_TYPE_MESSAGES,
  SEARCH_URL,
  offerStatuses,
} from "../../constants";
import styles from "./style.module.css";
import Teacher from "../../assets/images/avatar.jpg";
import filterIcon from "../../assets/images/filter_svg.svg";
import Chat from "../../components/chat/chat";
import { baseUrl } from "../../services/api";
import { useDebounce } from "../../helper/useDebounce";

const INITIAL_OFFER_DATA = { offerType: DEMO_TYPE };
const ITEMS_PER_PAGE = 6;
const INITIAL_FILTERS = [
  {
    filterHeader: "Gender",
    isOpen: true,
    filterOptions: [
      { id: "male", name: "Male", checked: false },
      { id: "female", name: "Female", checked: false },
    ],
  },
  {
    filterHeader: "Teaching Mode",
    isOpen: true,
    filterOptions: [
      { id: "online", name: "Online", checked: false },
      { id: "onsite", name: "Home", checked: false },
      { id: "both", name: "Both", checked: false },
    ],
  },
];

const VerticalFilterComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const dispatch = useDispatch();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isLargeScreen = screens.xl;
  const isMed = screens.sm && screens.md && screens.lg;
  const isSmall = (screens.xs || screens.sm ) && !screens.md


  // State management
  const [subjectSearch, setSubjectSearch] = useState(
    queryParams.get("query") || ""
  );
  const [selectedGender, setSelectedGender] = useState(
    queryParams.get("gender") || ""
  );
  const [modeOfTeaching, setModeOfTeaching] = useState(
    queryParams.get("modeOfTeaching") || ""
  );
  const [selectedCity, setSelectedCity] = useState(
    queryParams.get("cityId") || ""
  );
  const [selectedArea, setSelectedArea] = useState(
    queryParams.get("areaId") || ""
  );
  const [selectedCourseLevel, setSelectedCourseLevel] = useState(
    queryParams.get("course-level") || ""
  );
  const [qualificationLevel, setQualificationLevel] = useState(
    queryParams.get("qualification-level") || ""
  );
  const [selectedTeachingBoard, setSelectedTeachingBoard] = useState(
    queryParams.get("education-board") || ""
  );
  const [sortBy, setSortBy] = useState(queryParams.get("sortBy") || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(queryParams.get("page") || "1")
  );
  const [totalPages, setTotalPages] = useState(1);
  const [teachersData, setTeachersData] = useState([]);
  const [multiFilter, setMultiFilter] = useState([...INITIAL_FILTERS]);
  const [loading, setLoading] = useState({
    courseSearchLoading: true,
    reviewsLoading: false,
    detailsLoading: false,
  });
  const [offerDetail, setOfferDetail] = useState({ ...INITIAL_OFFER_DATA });
  const [selectedItem, setSelectedItem] = useState({
    itemDetail: "",
    open: false,
  });
  const [openScheduleDemoModal, setOpenScheduleDemoModal] = useState({
    itemDetail: "",
    open: false,
  });
  const [openMessageModal, setOpenMessageModal] = useState({
    itemDetail: "",
    open: false,
  });
  const [reviewModal, setReviewModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [teacherDetail, setTeacherDetail] = useState(false);
  const [teacherDetailsCache, setTeacherDetailsCache] = useState({});
  const [searchCache, setSearchCache] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const videoRef = useRef(null);
  const { listLoading } = useSelector((state) => state.courses);
  const loggedInUser = useSelector((state) => state.auth.user);

  // Create debounced search function
  const debouncedSearch = useDebounce((subject, page) => {
    searchCourseData(subject, page);
  }, 1500);

  // Fetch courses when parameters change
  useEffect(() => {
    searchCourseData(subjectSearch, currentPage);
    // eslint-disable-next-line
  }, [
    selectedGender,
    modeOfTeaching,
    selectedCity,
    selectedArea,
    selectedCourseLevel,
    qualificationLevel,
    selectedTeachingBoard,
    sortBy,
    currentPage,
  ]);

  // Fetch filters on mount
  useEffect(() => {
    fetchFilters();
  }, []);

  // Fetch city areas when city changes
  useEffect(() => {
    if (selectedCity) getCityAreas(selectedCity);
  }, [selectedCity]);

  const fetchFilters = async () => {
    try {
      const [cities, courseLevels, qualificationLevels, educationBoards] =
        await Promise.all([
          fetchLimitedCities(),
          fetchCourseLevels(),
          fetchQualificationLevels(),
          fetchEducationBoards(),
        ]);

      const filters = [...INITIAL_FILTERS];
      if (cities.status === 200 && cities.data?.data?.length) {
        filters.push({ filterHeader: "City", filterOptions: cities.data.data });
      }
      if (courseLevels.status === 200 && courseLevels.data?.data?.length) {
        filters.push({
          filterHeader: "Course Level",
          filterOptions: courseLevels.data.data,
        });
      }
      if (
        qualificationLevels.status === 200 &&
        qualificationLevels.data?.data?.length
      ) {
        filters.push({
          filterHeader: "Qualification Level",
          filterOptions: qualificationLevels.data.data,
        });
      }
      if (
        educationBoards.status === 200 &&
        educationBoards.data?.data?.length
      ) {
        filters.push({
          filterHeader: "Education Board",
          filterOptions: educationBoards.data.data,
        });
      }
      setMultiFilter(filters);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const getCityAreas = async (cityId) => {
    try {
      const { status, data } = await fetchCityArea(cityId);
      if (status === 200 && data?.data?.length) {
        setMultiFilter((prev) => [
          ...prev.filter((f) => f.filterHeader !== "Area"),
          { filterHeader: "Area", filterOptions: data.data },
        ]);
      }
    } catch (error) {
      console.error("Error fetching city areas:", error);
    }
  };

  const searchCourseData = async (subject, page) => {
    try {
      const cacheKey = `query=${subject}&gender=${selectedGender}&modeOfTeaching=${modeOfTeaching}&cityId=${selectedCity}&areaId=${selectedArea}&course-level=${selectedCourseLevel}&qualification-level=${qualificationLevel}&education-board=${selectedTeachingBoard}&sortBy=${sortBy}&page=${page}`;

      if (searchCache[cacheKey]) {
        const cached = searchCache[cacheKey];
        setTeachersData(cached.data);
        setTotalPages(cached.totalPages);
        setCurrentPage(cached.currentPage);
        updateURL(subject, page);
        return;
      }

      setLoading((prev) => ({ ...prev, courseSearchLoading: true }));
      updateURL(subject, page);
      const { status, data } = await fetchCoursesDataOnSearch2(
        subject,
        selectedGender,
        modeOfTeaching,
        selectedCity,
        selectedArea,
        selectedCourseLevel,
        qualificationLevel,
        sortBy,
        page
      );

      if (status === 200) {
        const teachers = data?.data || [];
        const totalP = data?.totalPages || 1;
        const currentP = Number(data?.currentPage) || page;

        setTeachersData(teachers);
        setTotalPages(totalP);
        setCurrentPage(currentP);

        setSearchCache((prev) => ({
          ...prev,
          [cacheKey]: { data: teachers, totalPages: totalP, currentPage: currentP },
        }));
      } else if (status === 401) {
        toast.error(data?.message);
      } else {
        toast.error(data?.message || "Error fetching courses");
      }
    } catch (error) {
      toast.error("Error fetching courses");
    } finally {
      setLoading((prev) => ({ ...prev, courseSearchLoading: false }));
    }
  };

  const handleViewDetails = async (teacher) => {
    try {
      if (teacherDetailsCache[teacher.id]) {
        setTeacherDetail(teacherDetailsCache[teacher.id]);
        return;
      }

      setTeacherDetail(teacher);
      if (!teacher?.id) return;

      setLoading((prev) => ({ ...prev, detailsLoading: true }));

      const [
        educationsRes,
        academicRes,
        corporateRes,
        boardsRes,
        mediaRes
      ] = await Promise.all([
        fetchTeacherEducations(teacher.id),
        fetchTeacherAcademicExperiance(teacher.id),
        fetchTeacherCorporateExperiance(teacher.id),
        fetchTeacherTeachingBoards(teacher.id),
        fetchTeacherDocuments(teacher.id, {
          types: ["profilePicture", "coverImage", "introVideos"],
        }),
      ]);

      const mergedData = {
        ...teacher,
        teacherEducations: educationsRes?.data?.data || [],
        teacherAcademicExperiences: academicRes?.data?.data || [],
        teacherCorporateExperiences: corporateRes?.data?.data || [],
        teacherBoards: boardsRes?.data?.data || [],
        teacherMedia: mediaRes?.data?.data || teacher.teacherMedia || [],
      };

      setTeacherDetailsCache((prev) => ({
        ...prev,
        [teacher.id]: mergedData,
      }));
      setTeacherDetail(mergedData);
    } catch (error) {
      console.error("Error fetching teacher details:", error);
    } finally {
      setLoading((prev) => ({ ...prev, detailsLoading: false }));
    }
  };

  const updateURL = (subject, page) => {
    navigate(
      `${SEARCH_URL}?query=${subject}&gender=${selectedGender}&modeOfTeaching=${modeOfTeaching}&cityId=${selectedCity}&areaId=${selectedArea}&course-level=${selectedCourseLevel}&qualification-level=${qualificationLevel}&education-board=${selectedTeachingBoard}&sortBy=${sortBy}&page=${page}`
    );
  };

  const onSelectFilter = async (
    e,
    headerIndex,
    headerOptionIndex,
    filterHeader,
    filterOption
  ) => {
    try {
      setCurrentPage(1);
      const { value, checked } = e.target;
      const prevFilters = [...multiFilter];
      prevFilters[headerIndex].filterOptions = prevFilters[
        headerIndex
      ].filterOptions.map((opt, idx) => ({
        ...opt,
        checked: idx === headerOptionIndex ? checked : false,
      }));
      setMultiFilter(prevFilters);

      let gender = selectedGender;
      let city = selectedCity;
      let teachingMode = modeOfTeaching;
      let area = selectedArea;
      let level = selectedCourseLevel;
      let qualification = qualificationLevel;
      let teachingBoard = selectedTeachingBoard;

      if (filterHeader === "Gender") {
        gender = checked ? filterOption : "";
        setSelectedGender(gender);
      } else if (filterHeader === "Course Level") {
        level = checked ? value : "";
        setSelectedCourseLevel(level);
      } else if (filterHeader === "Education Board") {
        teachingBoard = checked ? value : "";
        setSelectedTeachingBoard(teachingBoard);
      } else if (filterHeader === "Qualification Level") {
        qualification = checked ? value : "";
        setQualificationLevel(qualification);
      } else if (filterHeader === "City") {
        city = checked ? value : "";
        setSelectedCity(city);
        setSelectedArea("");
        if (checked) await getCityAreas(value);
      } else if (filterHeader === "Area") {
        area = checked ? value : "";
        setSelectedArea(area);
      } else if (filterHeader === "Teaching Mode") {
        teachingMode = checked ? filterOption : "";
        setModeOfTeaching(teachingMode);
      }

      setLoading((prev) => ({ ...prev, courseSearchLoading: true }));

      const cacheKey = `query=${subjectSearch}&gender=${gender}&modeOfTeaching=${teachingMode}&cityId=${city}&areaId=${area}&course-level=${level}&qualification-level=${qualification}&education-board=${teachingBoard}&sortBy=${sortBy}&page=1`;

      if (searchCache[cacheKey]) {
        const cached = searchCache[cacheKey];
        setTeachersData(cached.data);
        setTotalPages(cached.totalPages);
        setCurrentPage(cached.currentPage);
        updateURL(subjectSearch, 1);
        setLoading((prev) => ({ ...prev, courseSearchLoading: false }));
        return;
      }

      const { status, data } = await fetchCoursesDataOnSearch2(
        subjectSearch,
        gender,
        teachingMode,
        city,
        area,
        level,
        qualification,
        sortBy,
        1
      );

      if (status === 200) {
        const teachers = data?.data || [];
        const totalP = data?.totalPages || 1;
        const currentP = Number(data?.currentPage) || 1;

        setTeachersData(teachers);
        setTotalPages(totalP);
        setCurrentPage(currentP);

        setSearchCache((prev) => ({
          ...prev,
          [cacheKey]: { data: teachers, totalPages: totalP, currentPage: currentP },
        }));
      } else if (status === 401) {
        toast.error(data?.message);
      } else {
        toast.error(data?.message || "Error applying filters");
      }
    } catch (error) {
      toast.error("Error applying filters");
    } finally {
      setLoading((prev) => ({ ...prev, courseSearchLoading: false }));
    }
  };

  const clearFilter = async (filterHeader) => {
    try {
      setCurrentPage(1);
      const resetFilters = multiFilter.map((filter) => {
        if (filter.filterHeader === filterHeader) {
          return {
            ...filter,
            filterOptions: filter.filterOptions.map((opt) => ({
              ...opt,
              checked: false,
            })),
          };
        }
        return filter;
      });
      setMultiFilter(resetFilters);

      let gender = selectedGender;
      let city = selectedCity;
      let mode = modeOfTeaching;
      let area = selectedArea;
      let level = selectedCourseLevel;
      let qualification = qualificationLevel;
      let teachingBoard = selectedTeachingBoard;

      if (filterHeader === "Gender") setSelectedGender((gender = ""));
      if (filterHeader === "Education Board")
        setSelectedTeachingBoard((teachingBoard = ""));
      if (filterHeader === "Qualification Level")
        setQualificationLevel((qualification = ""));
      if (filterHeader === "Course Level") setSelectedCourseLevel((level = ""));
      if (filterHeader === "City") {
        setSelectedCity((city = ""));
        setSelectedArea((area = ""));
      }
      if (filterHeader === "Area") setSelectedArea((area = ""));
      if (filterHeader === "Teaching Mode") setModeOfTeaching((mode = ""));

      setLoading((prev) => ({ ...prev, courseSearchLoading: true }));

      const cacheKey = `query=${subjectSearch}&gender=${gender}&modeOfTeaching=${mode}&cityId=${city}&areaId=${area}&course-level=${level}&qualification-level=${qualification}&education-board=${teachingBoard}&sortBy=${sortBy}&page=1`;

      if (searchCache[cacheKey]) {
        const cached = searchCache[cacheKey];
        setTeachersData(cached.data);
        setTotalPages(cached.totalPages);
        setCurrentPage(cached.currentPage);
        updateURL(subjectSearch, 1);
        setLoading((prev) => ({ ...prev, courseSearchLoading: false }));
        return;
      }

      const { status, data } = await fetchCoursesDataOnSearch2(
        subjectSearch,
        gender,
        mode,
        city,
        area,
        level,
        qualification,
        sortBy,
        1
      );

      updateURL(subjectSearch, 1);
      if (status === 200) {
        const teachers = data?.data || [];
        const totalP = Math.ceil((data?.total || 0) / ITEMS_PER_PAGE);
        const currentP = 1;

        setTeachersData(teachers);
        setTotalPages(totalP);
        setCurrentPage(currentP);

        setSearchCache((prev) => ({
          ...prev,
          [cacheKey]: { data: teachers, totalPages: totalP, currentPage: currentP },
        }));
      } else if (status === 401) {
        toast.error(data?.message);
      } else {
        toast.error(data?.message || "Error clearing filters");
      }
    } catch (error) {
      toast.error("Error clearing filters");
    } finally {
      setLoading((prev) => ({ ...prev, courseSearchLoading: false }));
    }
  };

  const handleCollapseFilter = (index, status) => {
    setMultiFilter((prev) =>
      prev.map((filter, i) =>
        i === index ? { ...filter, isOpen: status } : filter
      )
    );
  };

  const handleVideoModal = (status, item) => {
    setSelectedItem({ open: status, itemDetail: item });
  };

  const handleCloseVideoModal = () => {
    if (videoRef.current) videoRef.current.pause();
    handleVideoModal(false, "");
  };

  const handleScheduleDemoModal = (status, item) => {
    setOpenScheduleDemoModal({ open: status, itemDetail: item });
  };

  const handleMessageModal = (status, item) => {
    setOpenMessageModal({ open: status, itemDetail: item });
  };

  const handleShowReviews = async (teacherId) => {
    try {
      setReviewModal(true);
      setLoading((prev) => ({ ...prev, reviewsLoading: true }));
      const { status, data } = await getTeacherReviews(teacherId);
      if (status === 200) setReviews(data?.data || []);
    } catch (error) {
      toast.error("Error fetching reviews");
    } finally {
      setLoading((prev) => ({ ...prev, reviewsLoading: false }));
    }
  };

  const sendOffer = async (offerData) => {
    try {
      const receiverProfileUrl = openScheduleDemoModal?.itemDetail?.teacherMedia?.find(
        (x) => x?.type === "profilePicture"
      )?.url;
      const messageBody = {
        text: OFFER_TYPE_MESSAGES.DEMO,
        sender_uuid: loggedInUser?.uuid,
        senderName: loggedInUser?.fullName,
        senderRole: loggedInUser?.role || "",
        receiver_uuid: openScheduleDemoModal?.itemDetail?.uuid,
        receiverId: openScheduleDemoModal?.itemDetail?.id,
        receiverName: openScheduleDemoModal?.itemDetail?.fullName,
        timestamp: new Date().toISOString(),
        seen: false,
        offerDetail: { ...offerData, offerStatus: offerStatuses.PENDING },
      };
      const localProfilePic = localStorage.getItem("profilePicture");
      const senderProfileUrl = localProfilePic || loggedInUser?.profilePicture;
      await addDocToFirestore(loggedInUser?.uuid, openScheduleDemoModal?.itemDetail?.uuid, messageBody, senderProfileUrl, receiverProfileUrl);
      handleScheduleDemoModal(false, {});
    } catch (error) {
      toast.error("Error sending offer");
    }
  };

  const filterIntroVideo = (list) => {
    const introVideo = list?.find((item) => item?.type === "introVideos");
    return introVideo ? `${baseUrl}${introVideo?.url}` : null;
  };

  const filterExpertiseSubject = (subjects) => {
    const expertSubjects = subjects.filter((subj) => subj.expert);
    const selectedSubjects =
      expertSubjects.length > 0 ? expertSubjects : subjects.slice(0, 3);
    return selectedSubjects.map((subj) => subj.courseSubjectName).join(", ");
  };

  const LayoutRender = loggedInUser?.role === "student" ? StudentLayout : Layout;
  const numberOfSkeletons = !loggedInUser?.role ? [1, 2, 3] : [1, 2, 3, 4];

  return (
    <LayoutRender>
      <Helmet>
        <title>
          Search Tutors | Find Your Ideal Private Teacher by Subject, City &
          More | Tutify.online
        </title>
        <meta
          name="description"
          content="Discover expert tutors for any subject and level on Tutify.online.
          Our advanced filters help you find the ideal online or in-person teacher based on your specific needs."
        />
        <meta
          name="keywords"
          content="find a teacher online, search for tutors, online private tutors, home tutors near me, book a tutor,
          private teacher directory, qualified online teachers, find tutors by subject, tutors by course level,
          tutors by education board, connect with teachers directly, best online tutoring platform, schedule tutor demo"
        />
      </Helmet>

      <CreateOfferCard
        setOfferDetail={setOfferDetail}
        offerDetail={offerDetail}
        teacherId={openScheduleDemoModal?.itemDetail.id}
        studentId={loggedInUser?.id}
        onSendOffer={sendOffer}
        handleOk={() => handleScheduleDemoModal(false, {})}
        handleCancel={() => handleScheduleDemoModal(false, {})}
        open={openScheduleDemoModal?.open}
      />

      {reviewModal && (
        <Modal
          title="Teacher Review"
          open={reviewModal}
          onCancel={() => {
            setReviewModal(false);
            setReviews([]);
          }}
          footer={null}
        >
          {loading.reviewsLoading ? (
            "Loading..."
          ) : reviews?.length ? (
            <div
              className={`row pb-3 overflow-y-scroll ${styles.reviews} ${styles.scrollbar_custom}`}
            >
              {reviews
                .filter((review) => review?.review)
                .map((review, index) => (
                  <div
                    key={index}
                    className={`my-1 bg-light pt-1 pl-2 ${styles.border_color}`}
                  >
                    <div className="fs_12 mb-1 px-2">{review?.review}</div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="fs_12">No reviews available.</p>
          )}
          <div className="d-flex justify-content-end">
            <button
              onClick={() => {
                setReviewModal(false);
                setReviews([]);
              }}
              className="btn btn-primary btn_contained"
            >
              Ok
            </button>
          </div>
        </Modal>
      )}

      <Modal
        maskClosable={false}
        width={700}
        footer={null}
        open={selectedItem?.open}
        onOk={handleCloseVideoModal}
        onCancel={handleCloseVideoModal}
      >
        <div className="m-4">
          <video
            key={selectedItem?.itemDetail}
            ref={videoRef}
            autoPlay
            controls
            controlsList="nodownload"
            width={600}
            height={400}
          >
            <source src={selectedItem?.itemDetail} type="video/mp4" />
          </video>
        </div>
      </Modal>

      <Modal
        maskClosable={false}
        width={700}
        footer={null}
        closable
        open={openMessageModal?.open}
        onOk={() => handleMessageModal(false, "")}
        onCancel={() => {
          handleMessageModal(false, "");
          setProfileImage(null);
        }}
      >
        <Chat
          profileImage={profileImage}
          receiver={openMessageModal?.itemDetail}
          isModal={true}
        />
      </Modal>

      <h1
        className={`pageTitle ${loggedInUser?.role !== "student" && "mt-4"}`}
        style={
          loggedInUser?.role === "student"
            ? {}
            : {
                padding: isLargeScreen
                  ? `0px 110px`
                  : isMed
                  ? `0px 60px`
                  : isSmall
                  ? "0px 15px"
                  : `0px 50px`,
              }
        }
      >
        Find a Teacher
        {/* <hr /> */}
      </h1>

      {listLoading ? (
        <CenterLoading />
      ) : (
        <div className="container-fluid">
          {isFilterDrawerOpen && (
            <div
              className={`${!loggedInUser ? "d-lg-none" : ""}`}
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.4)",
                zIndex: 9999,
                display: "flex",
                justifyContent: "flex-end",
              }}
              onClick={() => setIsFilterDrawerOpen(false)}
            >
              <div
                className={`bg-white p-3 ${styles["filter-container"]}`}
                style={{
                  minWidth: 280,
                  maxWidth: 340,
                  width: "80vw",
                  height: "100vh",
                  overflowY: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="fw-bold fs_16">Filters</span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setIsFilterDrawerOpen(false)}
                    aria-label="Close Filters"
                  >
                    ×
                  </button>
                </div>
                <FilterSidebar
                  subjectSearch={subjectSearch}
                  setSubjectSearch={setSubjectSearch}
                  searchCourseData={searchCourseData}
                  multiFilter={multiFilter}
                  loading={loading}
                  onSelectFilter={onSelectFilter}
                  handleCollapseFilter={handleCollapseFilter}
                  clearFilter={clearFilter}
                  styles={styles}
                />
              </div>
            </div>
          )}

          <div
            className={`row ${
              (!loggedInUser || loggedInUser?.role === "teacher") &&
              isLargeScreen
                ? ""
                : "pb-4"
            }`}
            style={
              loggedInUser?.role === "student"
                ? {}
                : {
                    padding: isLargeScreen
                      ? `25px 110px`
                      : isMed
                      ? `25px 60px`
                      : isSmall
                      ? "25px 15px"
                      : `25px 50px`,
                  }
            }
          >
            {!loggedInUser?.role && (
              <div className={`col-md-3 d-none d-lg-block`}>
                <FilterSidebar
                  subjectSearch={subjectSearch}
                  setSubjectSearch={setSubjectSearch}
                  searchCourseData={searchCourseData}
                  multiFilter={multiFilter}
                  loading={loading}
                  onSelectFilter={onSelectFilter}
                  handleCollapseFilter={handleCollapseFilter}
                  clearFilter={clearFilter}
                  styles={styles}
                />
              </div>
            )}

            <div
              className={`${
                !loggedInUser?.role ? "col-md-12 col-lg-9" : "col-md-12"
              } p-0`}
            >
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between w-100 gap-2">
                  <Input
                    placeholder="Search for subjects"
                    prefix={
                      <span style={{ paddingRight: "8px" }}>
                        {" "}
                        {/* Add padding to create space */}
                        <AiOutlineSearch size={22} color="#999" />
                      </span>
                    }
                    style={{
                      backgroundColor: "#F8F9FA",
                      paddingTop: "5px",
                      paddingBottom: "5px",
                      border: "none",
                      borderRadius: "5px",
                    }}
                    value={subjectSearch}
                    onChange={(e) => {
                      setSubjectSearch(e.target.value);
                      debouncedSearch(e.target.value, 1);
                    }}
                    onPressEnter={(e) => searchCourseData(e.target.value, 1)}
                    allowClear
                    size="large"
                  />{" "}
                  <button
                    type="button"
                    className={`btn btn-light ${
                      !loggedInUser?.role ? "d-lg-none" : ""
                    } `}
                    style={{ border: "none" }}
                    onClick={() => setIsFilterDrawerOpen((prev) => !prev)}
                    aria-label="Show Filters"
                  >
                    {/* <AiOutlineFilter size={22} /> */}
                    <img
                      src={filterIcon}
                      alt="Filter"
                      style={{
                        width: 22,
                        height: 22,
                        objectFit: "contain",
                        color: "#EBEBEB",
                      }}
                    />
                  </button>
                </div>
              </div>

              {!loggedInUser?.role && (
                <div className="sky-bg-color p-2 mb-4">
                  <span className="fs_14">
                    You need to <b>Login</b> first to Send message or Schedule a
                    demo.
                  </span>
                </div>
              )}

              <div className="row">
                {loading.courseSearchLoading ? (
                  numberOfSkeletons.map((_, index) => (
                    <div
                      key={index}
                      className={`col-12 col-md-4 ${
                        !loggedInUser?.role ? "col-lg-4" : "col-lg-3"
                      } mb-4`}
                    >
                      <div className="placeholder-glow">
                        <div className="card h-100">
                          <div
                            className="placeholder card-img-top"
                            style={{ height: "200px" }}
                          ></div>
                          <div className="card-body">
                            <p className="placeholder col-6"></p>
                            <p className="placeholder col-4"></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : teachersData?.length ? (
                  <>
                    {teachersData.map((item) => {
                      const introVideo = filterIntroVideo(item?.teacherMedia);
                      const profileImage =
                        filterProfilePicture(
                          item?.teacherMedia,
                          "profilePicture"
                        ) || Teacher;
                      const coverImage =
                        filterProfilePicture(
                          item?.teacherMedia,
                          "coverImage"
                        ) || "";
                      const qualificationList =
                        multiFilter.find(
                          (item) => item.filterHeader === "Qualification Level"
                        )?.filterOptions || [];
                      const qualificationName =
                        qualificationList.find(
                          (qual) => qual.id === item?.qualificationLevelId
                        )?.name || "";
                      const isFavorite = item?.favouriteTeachers?.some(
                        (x) => x?.studentId === loggedInUser?.id
                      );

                      return (
                        <div
                          key={item?.id}
                          className={`col-12 col-md-4 ${
                            !loggedInUser?.role ? "col-lg-4" : "col-lg-3"
                          } mb-4`}
                        >
                          <FiverrCard
                            videoPreview={introVideo}
                            image={profileImage}
                            coverImage={coverImage}
                            videoThumbnail={filterProfilePicture(
                              item?.teacherMedia
                            )}
                            onPlayVideo={() =>
                              handleVideoModal(true, introVideo)
                            }
                            title={item?.fullName}
                            description={filterExpertiseSubject(item?.courses)}
                            intro={item?.intro}
                            rating={Math.round(parseInt(item?.rating))}
                            reviewsCount={item?.ratingCount || 0}
                            price={item?.price || "-"}
                            onViewDetails={() => handleViewDetails(item)}
                            onSendMessage={() => handleMessageModal(true, item)}
                            setProfileImage={setProfileImage}
                            onScheduleDemo={() =>
                              handleScheduleDemoModal(true, item)
                            }
                            handleShowReviews={() =>
                              handleShowReviews(item?.id)
                            }
                            teacherId={item?.id}
                            teacherUuid={item?.uuid}
                            qualificationName={qualificationName}
                            isFavorite={isFavorite}
                          />
                        </div>
                      );
                    })}
                    <div className="d-flex justify-content-center mb-4">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    </div>
                  </>
                ) : (
                  <p className="d-flex justify-content-center align-items-center fs_12 vh-100">
                    No record found.
                  </p>
                )}
              </div>
            </div>

            <TeacherDetailPopup
              visible={!!teacherDetail}
              onClose={() => setTeacherDetail("")}
              teacherDetail={teacherDetail}
              isLoading={loading.detailsLoading}
            />
          </div>
        </div>
      )}
    </LayoutRender>
  );
};

export default VerticalFilterComponent;
